
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.productList = document.querySelector(`ul[data-id="${this.sectionId}"]`);
      this.loadMoreButton = this.querySelector('.btn--load-more');
      this.paginationType = this.getAttribute('data-type');
      this.unavailableProductsLast = this.productList?.dataset.unavailableProductsLast === 'true';
      this.availableCount = parseInt(this.productList?.dataset.availableCount, 10) || 0;
      this.unavailableCount = parseInt(this.productList?.dataset.unavailableCount, 10) || 0;
      this.availabilityPhase = this.productList?.dataset.availabilityPhase || 'available';
      this.countItem = parseInt(this.productList?.getAttribute('data-products-count'), 10) || 0;
      this.currentPage = 2;
      this.scrollObserver = null;
    }

    connectedCallback() {
      if (this.dataset.appendUnavailableTail === 'true') {
        this.appendUnavailableTail();
        return;
      }

      this.paginationType === 'load-more-button'
        ? this.setupLoadMoreButton()
        : this.paginationType === 'infinite-scroll'
        ? this.setupInfiniteScroll()
        : null;

      this.restoreScrollState();
      this.bindProductLinks();
    }

    getLoadedProductCount() {
      return this.productList?.querySelectorAll('li.grid__item').length || 0;
    }

    getTotalExpectedCount() {
      return this.unavailableProductsLast
        ? this.availableCount + this.unavailableCount
        : this.countItem;
    }

    shouldSwitchToUnavailablePhase() {
      return (
        this.unavailableProductsLast &&
        this.availabilityPhase === 'available' &&
        this.unavailableCount > 0 &&
        this.getLoadedProductCount() >= this.availableCount
      );
    }

    switchToUnavailablePhase() {
      this.availabilityPhase = 'unavailable';
      this.currentPage = 1;
      this.productList?.setAttribute('data-availability-phase', 'unavailable');
    }

    shouldFinalizePagination() {
      return this.getLoadedProductCount() >= this.getTotalExpectedCount();
    }

    bindProductLinks() {
      this.productList?.querySelectorAll('.grid__item a').forEach(link => {
        link.addEventListener('click', () => {
          sessionStorage.setItem('pagination-scrollY', window.scrollY);
          sessionStorage.setItem('pagination-currentPage', this.currentPage - 1);
        });
      });
    }

    setupLoadMoreButton() {
      this.loadMoreButton?.addEventListener('click', () => this.loadNextPage());
    }

    setupInfiniteScroll() {
      this.scrollObserver = new IntersectionObserver(
        entries => entries[0].isIntersecting && this.loadNextPage(),
        { threshold: [0, 1] }
      );
      this.scrollObserver.observe(this);
    }

    buildUrlWithPageNumber(search = '', key = 'page') {
      const urlParams = new URLSearchParams(search);
      urlParams.set(key, this.currentPage);

      if (this.unavailableProductsLast) {
        if (this.availabilityPhase === 'available') {
          urlParams.set('filter.v.availability', '1');
        } else if (this.availabilityPhase === 'unavailable') {
          urlParams.set('filter.v.availability', '0');
        }
      }

      return `?${urlParams.toString()}`;
    }

    async loadNextPage() {
      if (this.hasAttribute('loading') || !this.productList) return;

      try {
        this.setAttribute('loading', true);
        this.toggleLoadingSpinner(true);

        const url = `${document.location.pathname}${this.buildUrlWithPageNumber(document.location.search)}`;
        const response = await fetch(url);
        const html = await response.text();

        const shouldContinue = await this.renderFetchedProducts(html);
        if (shouldContinue) return;

        this.currentPage++;
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        this.toggleLoadingSpinner(false);
        this.removeAttribute('loading');
      }
    }

    async renderFetchedProducts(html) {
      const newProductGrid = new DOMParser()
        .parseFromString(html, 'text/html')
        .getElementById('product-grid');

      if (!newProductGrid?.children.length) {
        if (this.shouldSwitchToUnavailablePhase()) {
          this.switchToUnavailablePhase();
          await this.loadNextPage();
          return true;
        }

        this.finalizePagination();
        return true;
      }

      newProductGrid.querySelectorAll('.grid__item').forEach(product => {
        this.productList.appendChild(product);
      });

      if (this.shouldSwitchToUnavailablePhase()) {
        this.switchToUnavailablePhase();
        await this.loadNextPage();
        return true;
      }

      if (this.shouldFinalizePagination()) {
        this.finalizePagination();
        return true;
      }

      this.bindProductLinks();
      return false;
    }

    async appendUnavailableTail() {
      if (!this.unavailableProductsLast || this.unavailableCount === 0) return;

      this.switchToUnavailablePhase();

      while (!this.shouldFinalizePagination()) {
        await this.loadNextPage();
      }
    }

    toggleLoadingSpinner(isLoading) {
      if (!this.loadMoreButton) return;

      const spinner = this.loadMoreButton.querySelector('.loading__spinner');
      this.loadMoreButton.classList.toggle('loading', isLoading);
      spinner?.classList.toggle('hidden', !isLoading);
    }

    finalizePagination() {
      this.scrollObserver?.unobserve(this);
      this.scrollObserver?.disconnect();
      this.loadMoreButton?.classList.add('visually-hidden');
    }

    disconnectedCallback() {
      this.scrollObserver?.disconnect();
      this.loadMoreButton?.removeEventListener('click', this.loadNextPage);
    }

    async restoreScrollState() {
      const savedPage = parseInt(sessionStorage.getItem('pagination-currentPage'), 10);
      const savedScrollY = parseInt(sessionStorage.getItem('pagination-scrollY'), 10);

      if (!isNaN(savedPage) && savedPage >= 2) {
        await this.loadPagesUpTo(savedPage);

        window.setTimeout(() => {
          window.scrollTo(0, savedScrollY);
        }, 100);
        sessionStorage.removeItem('pagination-currentPage');
        sessionStorage.removeItem('pagination-scrollY');
      }
    }

    async loadPagesUpTo(page) {
      while (this.currentPage <= page && !this.shouldFinalizePagination()) {
        await this.loadNextPage();
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
